define([
  'underscore',
  'immutable',
  'es6!sumo/util',
  'es6!launcher/util/listbuilder/constants',
  'es6!lib/sumome/react/util/sentryUtils',
], (_, Immutable, Util, Constants, SentryUtils) => {
  const { FormTypes } = Constants;
  const { containsPrize } = Util;

  function updateComponentGlobals(globals, component) {
    const meta = component.getIn(['shortcutMeta', 'meta']);
    if (!meta) return component;

    const updateGlobals = (v) => {
      let vars = v;

      vars.keySeq().forEach((key) => {
        const globalKey = vars.get(key);
        if (typeof globalKey === 'string' && globals.has(globalKey)) {
          vars = vars.set(key, globals.get(globalKey));
        } else if (Immutable.Map.isMap(globalKey)) {
          vars = vars.mergeIn([key], updateGlobals(globalKey));
        }
      });

      return vars;
    };

    return component.mergeDeep(updateGlobals(meta));
  }

  function cloneJSON(json) {
    return JSON.parse(JSON.stringify(json || {}));
  }

  function isPercent(shouldCheck, value) {
    return shouldCheck && value && value.toString().indexOf('%') !== -1;
  }

  function getPixelValue(pixelString) {
    // console.assert(!pixelString || pixelString.slice(-2) === 'px', 'getPixelValue received non px string');

    return parseInt(pixelString, 10);
  }

  function getPercentValue(percentString) {
    if (!percentString || !percentString.length) return NaN;
    if (!/^([1-9][0-9]*|0)%$/.test(percentString)) return NaN;

    return parseInt(percentString.substring(0, percentString.length - 1), 10);
  }

  function getRoundedPosition(position, respectPercentages) {
    return {
      left: isPercent(respectPercentages, position.left) ? position.left : Math.round(position.left),
      top: isPercent(respectPercentages, position.top) ? position.top : Math.round(position.top),
      width: isPercent(respectPercentages, position.width) ? position.width : Math.round(position.width),
      height: isPercent(respectPercentages, position.height) ? position.height : Math.round(position.height),
    };
  }

  function styleToPosition(style, respectPercentages) {
    const styleCopy = _.clone(style) || {};

    const left = isPercent(respectPercentages, styleCopy.left)
      ? styleCopy.left
      : (getPixelValue(styleCopy.left) || 0);

    const top = isPercent(respectPercentages, styleCopy.top)
      ? styleCopy.top
      : (getPixelValue(styleCopy.top) || 0);

    const width = isPercent(respectPercentages, styleCopy.width)
      ? styleCopy.width
      : (getPixelValue(styleCopy.width) || 0);

    const height = isPercent(respectPercentages, styleCopy.height)
      ? styleCopy.height
      : (getPixelValue(styleCopy.height) || 0);

    return { left, top, width, height };
  }

  function positionToStyle(position, respectPercentages) {
    const { left, top, width, height } = position;

    const style = {};

    if (isPercent(respectPercentages, left)) {
      style.left = left;
    } else if (typeof left !== 'undefined' && left !== null) {
      style.left = `${left}px`;
    }

    if (isPercent(respectPercentages, top)) {
      style.top = top;
    } else if (typeof top !== 'undefined' && top !== null) {
      style.top = `${top}px`;
    }

    if (isPercent(respectPercentages, width)) {
      style.width = width;
    } else if (typeof width !== 'undefined' && width !== null) {
      style.width = `${width}px`;
    }

    if (isPercent(respectPercentages, height)) {
      style.height = height;
    } else if (typeof height !== 'undefined' && height !== null) {
      style.height = `${height}px`;
    }

    return style;
  }

  function hasDiscount(popup, autoResponseEmails) {
    const parsedJson = popup.get('parsedJson');

    if (!parsedJson) return false;
    
    const goal = popup.get('goal');
    const subscribeRedirectUrl = popup.get('subscribe_redirect_url') || '';
    const conversionPixels = popup.get('pixels_conversion') || '';
    const components = parsedJson.getIn(['success', 'components']) || Immutable.List();

    for (let i = 0; i < components.size; i += 1) {
      const component = components.get(i);
      const type = component.get('type');
      const text = component.get('text');
      const to = component.get('to');
      const action = component.get('action');

      if (type === 'discount') {
        return true;
      }

      if (popup.get('form_type') !== 'game') {
        if (['text', 'button'].indexOf(type) > -1) {
          if (
            containsPrize(text)
            || (action === 'redirect' && containsPrize(to))
          ) {
            return true;
          }
        }
      }
    }

    // Check subscribe redirect URL and conversion pixels
    if (containsPrize(subscribeRedirectUrl) || containsPrize(conversionPixels)) return true;

    // TODO: Update this somehow
    // Check auto response emails if form is collecting emails
    if (goal === 'emails') {
      const autoResponses = popup.get('auto_responses');
      if (autoResponses && autoResponses.size) {
        for (let i = 0; i < autoResponses.size; i += 1) {
          if (autoResponses.getIn([i, 'status']) === 'deleted') continue;
          const rule = autoResponses.getIn([i, 'email']);
          const email = autoResponseEmails.find(e => e.get('id') === rule.get('id'));
          if (email && email.get('body') && email.get('body').indexOf('{discount}') > -1) {
            return true;
          }
        }
      }
    }

    return false;
  }

  // Checks game component and makes sure there is at least one discount campaign assigned.
  function hasValidPrizes(popup, discountCampaignIds) {
    const components = popup.getIn(['parsedJson', 'subscribe', 'components']);
    const gameComponent = components.find(component => component.get('type') === 'game');

    if (!gameComponent) return false;

    const options = gameComponent.get('options');

    const winningOption = options.find((option) => {
      const prizeType = option.get('prizeType');
      const discountCampaignId = option.get('discountCampaignId');
      const weight = option.get('weight');

      return (
        prizeType &&
        prizeType !== 'none' &&
        (prizeType !== 'discount' || discountCampaignIds.contains(discountCampaignId)) &&
        weight > 0
      );
    });

    return winningOption;
  }

  function hasDeletedPrizes(popup, discountCampaignIds) {
    const components = popup.getIn(['parsedJson', 'subscribe', 'components']);
    const gameComponent = components.find(component => component.get('type') === 'game');

    if (!gameComponent) return false;

    const options = gameComponent.get('options');

    const invalidDiscount = options.find(option => (
      option.get('prizeType') === 'discount' &&
      discountCampaignIds.indexOf(option.get('discountCampaignId')) === -1
    ));

    return !!invalidDiscount;
  }

  function canUseTabs(formType) {
    return [
      FormTypes.SCROLL_BOX,
      FormTypes.POPUP,
      FormTypes.CLICK_TRIGGER_POPUP,
      FormTypes.GAME,
    ].indexOf(formType) > -1;
  }

  function isInlineComponent(component) {
    return component.type !== 'background' && (component.type !== 'image' || (['close', 'optout'].indexOf(component.action) === -1 && !component.footer));
  }

  function getHeadingText(json) {
    const defaultHeading = 'Join Our Newsletter';
    try {
      if ('subscribe' in json) {
        const headingComponent = _.first(_.filter(json.subscribe.components, function(c) {
          return (c.type === 'text');
        }).sort(function(a, b) {
          const aFontSize = parseFloat((a.style.fontSize || '12').replace('px', ''));
          const bFontSize = parseFloat((b.style.fontSize || '12').replace('px', ''));

          return bFontSize - aFontSize;
        }));

        return headingComponent ? headingComponent.text : defaultHeading;
      }
    } catch(e) {
      return defaultHeading;
    }
  }

  function getReadableDate(date) {
    const month = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];
    return `${month[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  function generatePopupName(json, d = new Date()) {
    const headingText = getHeadingText(json);
    if (isNaN(d.getTime())) d = new Date();

    const date = getReadableDate(d);
    return `${date} - ${headingText}`;
  }

  const genId = () => {
    const r = () => `${Math.random().toString(16)}00000000`.substr(2, 8).toLowerCase();
    return r() + r() + r() + r() + r() + r() + r() + r();
  };

  /**
   * Check if any of the components have breakpoints defined.
   * @param popup The popup to check (in normal JSON format)
   * @param popupMode The popup mode to check
   * @returns True if any component has breakpoints, false otherwise
   */
  const hasBreakpoints = (popup, popupMode) => {
    try {
      if (!popup || !popupMode) {
        throw new Error('Invalid popup or popupMode on "hasBreakpoints" validation check.');
      }

      const components = popup.parsedJson?.[popupMode]?.components || [];

      for (let i = 0; i < components.length; i += 1) {
        const component = components[i];

        if (component.breakpoints && Object.keys(component.breakpoints).length > 0) {
          return true;
        }
      }
    } catch (err) {
      SentryUtils.captureValidationError(err);
    }

    // If no breakpoints are found, return false.
    return false;
  };

  /**
   * A helper function to flag all breakpoints except the selected one for review.
   * @param selectedBreakpoint The breakpoint that has been selected.
   * @param allBreakpointsSettings Map of all breakpoint settings.
   * @returns A new map with all breakpoints except the selected one flagged for review.
   */
  const flagBreakpointsForReview = (selectedBreakpoint, allBreakpointsSettings) => {
    const keysToReview = Object
      .keys(allBreakpointsSettings)
      .filter((key) => Number(key) !== selectedBreakpoint);

    return keysToReview.reduce((settings, key) => ({
      ...settings,
      [key]: { ...settings[key], shouldReview: true },
    }), { ...allBreakpointsSettings });
  };

  /**
   * A helper function to unflag a reviewed breakpoint.
   * @param reviewedBreakpoint The breakpoint that has been reviewed
   * @param allBreakpointsSettings Map of all breakpoint settings.
   * @returns A new map with the reviewed breakpoint unflagged for review.
   */
  const unflagReviewedBreakpoint = (
    reviewedBreakpoint,
    allBreakpointsSettings,
  ) => [reviewedBreakpoint].reduce((settings, key) => ({
    ...settings,
    [key]: { ...settings[key], shouldReview: false },
  }), { ...allBreakpointsSettings });

  /**
   * A helper function to check if there are any breakpoints flagged for review.
   * @param breakpointSettings Map of all breakpoint settings.
   * @returns A boolean indicating if there are any breakpoints flagged for review.
   */
  const hasFlaggedBreakpointsForReview = (breakpointSettings) => Object
    .values(breakpointSettings)
    .some((setting) => setting.shouldReview);

  /**
   * A helper function to check if a given size corresponds to a responsive breakpoint
   * @param size A breakpoint size in numeric format
   * @returns A boolean representing if the size corresponds to a responsive breakpoint
   */
  const MOBILE_BREAKPOINT_SIZE = 480;
  const DEFAULT_BREAKPOINT_SIZE = Infinity;

  const checkIfOnResponsiveSize = (size) => ![DEFAULT_BREAKPOINT_SIZE, undefined].includes(size);

  /**
   * Check if a given breakpoint size is the mobile breakpoint.
   * @param size A breakpoint size in numeric format
   * @returns True if the size is the mobile breakpoint (480), false otherwise
   */
  const isMobileBreakpoint = (size) => size === MOBILE_BREAKPOINT_SIZE;

  /**
   * Map a breakpoint size to its `responsiveHiddenOn` key. Mobile breakpoint hides
   * components flagged for 'small'; desktop hides those flagged for 'medium'.
   * @param size A breakpoint size in numeric format
   * @returns The corresponding `responsiveHiddenOn` key
   */
  const mapBreakpointToHiddenOnKey = (size) => (isMobileBreakpoint(size) ? 'small' : 'medium');

  /**
   * Map a breakpoint size to its CSS class suffix ('mobile' | 'default').
   * @param size A breakpoint size in numeric format
   * @returns The suffix used in `sumome-wysiwyg-breakpoint-${suffix}`
   */
  const mapBreakpointToSuffix = (size) => (isMobileBreakpoint(size) ? 'mobile' : 'default');

  /**
   * Map a CSS class suffix back to its breakpoint size.
   * @param suffix 'mobile' | 'default'
   * @returns The corresponding breakpoint size (480 or Infinity)
   */
  const mapSuffixToBreakpoint = (suffix) => (suffix === 'mobile' ? MOBILE_BREAKPOINT_SIZE : DEFAULT_BREAKPOINT_SIZE);

  /**
   * Returns the components for the given `popup` prepared for the given breakpoint.
   * @param  {Immutable.List} components  The popup to prepare responsive components from
   * @param  {String}         hiddenOnKey The `responsiveHiddenOn` key for the current
   *                                      breakpoint ('small' | 'medium')
   * @return {Immutable.List}             An Immutable List of components visible at the
   *                                      given breakpoint
   */
  function getComponentsForBreakpoint(components, hiddenOnKey) {
    const collect = (currentComponents) => {
      if (!currentComponents || !currentComponents.size) return [];

      let responsiveComponents = Immutable.List();
      currentComponents.forEach((component) => {
        const responsiveHiddenOn = component.get('responsiveHiddenOn');
        const childComponents = component.get('childComponents');

        if (responsiveHiddenOn && responsiveHiddenOn.get(hiddenOnKey)) return;

        if (childComponents && childComponents.size) {
          responsiveComponents = responsiveComponents.concat(collect(childComponents));
        } else {
          responsiveComponents = responsiveComponents.push(component);
        }
      });

      return responsiveComponents;
    };

    return collect(components);
  }

  return {
    canUseTabs,
    cloneJSON,
    generatePopupName,
    getPercentValue,
    getPixelValue,
    getRoundedPosition,
    hasDeletedPrizes,
    hasDiscount,
    hasValidPrizes,
    isInlineComponent,
    positionToStyle,
    styleToPosition,
    updateComponentGlobals,
    genId,
    hasBreakpoints,
    flagBreakpointsForReview,
    unflagReviewedBreakpoint,
    hasFlaggedBreakpointsForReview,
    checkIfOnResponsiveSize,
    isMobileBreakpoint,
    mapBreakpointToHiddenOnKey,
    mapBreakpointToSuffix,
    mapSuffixToBreakpoint,
    getComponentsForBreakpoint,
    MOBILE_BREAKPOINT_SIZE,
    DEFAULT_BREAKPOINT_SIZE,
  };
});
